import React, { useContext, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SortOrder } from 'primereact/api';
import { getEntities } from 'app/api/generalApi';
import { ToastContext } from 'app/context/ToastContext';
import { formatBigDecimalIntoString } from 'app/shared/util/number-utils';
import { boldNameFormatter } from 'app/shared/util/formatters';

const ReportTable: React.FC = () => {
  const { showError } = useContext(ToastContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState(null);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 20,
    page: 0,
    sortField: 'id',
    sortOrder: SortOrder.DESC,
  });

  const loadData = () => {
    setLoading(true);
    const sort = lazyParams.sortField + ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc');
    getEntities('/api/reports', { page: lazyParams.page, size: lazyParams.rows, sort })
      .then(response => {
        setReports(response.data.content);
        setTotalRecords(response.data.totalElements);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [lazyParams]);

  const formatAgent = v => {
    if (!v) return '';
    if (v.firstName) {
      return v.lastName ? `${v.firstName} ${v.lastName}` : v.firstName;
    }
    return v.name;
  };

  const allowExpansion = rowData =>
    (rowData.agentProvisions && rowData.agentProvisions.length > 0) ||
    (rowData.contributions && rowData.contributions.length > 0);

  const isNegativeRow = ap => ap.isStorno || (Number(ap.provision) || 0) < 0;

  const eur = v => `${formatBigDecimalIntoString(typeof v === 'number' ? v.toFixed(2) : v)} EUR`;

  const signed = (v, alwaysGreenIfPositive = false) => {
    const n = Number(v) || 0;
    const color = n < 0 ? '#e31818' : alwaysGreenIfPositive && n > 0 ? '#16a34a' : undefined;
    return <span style={color ? { color, fontWeight: 600 } : undefined}>{eur(v)}</span>;
  };

  const asDeduction = v => {
    const n = Math.abs(Number(v) || 0);
    return n === 0 ? <span>{eur(0)}</span> : <span style={{ color: '#e31818', fontWeight: 600 }}>{eur(-n)}</span>;
  };

  const formatPeriod = c => {
    if (!c.setFrom && !c.setTo) return '-';
    return `${c.setFrom ?? ''} – ${c.setTo ?? ''}`;
  };

  const expandTemplate = rowData => {
    const provs = rowData.agentProvisions || [];
    const contributions = rowData.contributions || [];

    let sumProvizie = 0;
    let sumStorno = 0;
    let sumKrytie = 0;
    let sumBonusy = 0;
    let sumZrazky = 0;
    let sumCelkom = 0;
    for (const c of contributions) {
      sumProvizie += Number(c.spoluProvizie) || 0;
      sumStorno += Number(c.spoluStorno) || 0;
      sumKrytie += Number(c.minusOdvodKvoliStornu) || 0;
      sumBonusy += Number(c.bonusy) || 0;
      sumZrazky += Number(c.zrazky) || 0;
      sumCelkom += Number(c.celkomNaVyplatu) || 0;
    }

    return (
      <div className="p-3">
        <h5>Zoznam zmlúv</h5>
        <DataTable value={provs} dataKey="id" size="small" stripedRows emptyMessage="Žiadne zmluvy.">
          <Column field="proposalNr" header="Číslo návrhu" />
          <Column field="client" header="Klient" />
          <Column field="agentName" header="Obchodník" />
          <Column field="insuranceCompany" header="Poisťovňa, produkt" />
          <Column field="isStorno" header="Typ" body={v => (isNegativeRow(v) ? 'storno' : 'provízia')} />
        </DataTable>

        <h5 className="mt-4">Detail dlhu</h5>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0 }}>
          Riadok = jeden provízny beh, ktorý prispel do dlhu. Súčet stĺpca <em>Príspevok do dlhu</em> sa rovná zostávajúcemu dlhu.
        </p>
        <DataTable value={contributions} dataKey="sheetId" size="small" stripedRows emptyMessage="Žiadne behy.">
          <Column field="period" header="Obdobie" body={formatPeriod} footer="Spolu" />
          <Column
            field="spoluProvizie"
            header="Provízie"
            body={c => signed(c.spoluProvizie, true)}
            footer={eur(sumProvizie)}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="spoluStorno"
            header="Storno"
            body={c => asDeduction(c.spoluStorno)}
            footer={
              <span style={{ color: '#e31818', fontWeight: 700 }}>{eur(-Math.abs(sumStorno))}</span>
            }
            style={{ textAlign: 'right' }}
          />
          <Column
            field="minusOdvodKvoliStornu"
            header="Krytie zo stornofondu"
            body={c => eur(c.minusOdvodKvoliStornu)}
            footer={eur(sumKrytie)}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="bonusy"
            header="Bonusy"
            body={c => signed(c.bonusy, true)}
            footer={eur(sumBonusy)}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="zrazky"
            header="Zrážky"
            body={c => signed(c.zrazky)}
            footer={eur(sumZrazky)}
            style={{ textAlign: 'right' }}
          />
          <Column
            field="celkomNaVyplatu"
            header="Príspevok do dlhu"
            body={c => signed(c.celkomNaVyplatu)}
            footer={
              <span style={{ color: sumCelkom < 0 ? '#e31818' : '#16a34a', fontWeight: 700 }}>{eur(sumCelkom)}</span>
            }
            style={{ textAlign: 'right' }}
          />
        </DataTable>
      </div>
    );
  };

  return (
    <div>
      <div className="card">
        <DataTable
          stripedRows
          value={reports}
          size="small"
          lazy
          responsiveLayout="scroll"
          dataKey="id"
          emptyMessage="Žiadne dáta."
          paginator
          first={lazyParams.first}
          rows={20}
          totalRecords={totalRecords}
          onPage={e => setLazyParams({ ...lazyParams, first: e.first, page: e.page, rows: e.rows })}
          onSort={e => setLazyParams({ ...lazyParams, sortField: e.sortField, sortOrder: e.sortOrder })}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          rowExpansionTemplate={expandTemplate}
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          loading={loading}
        >
          <Column expander={allowExpansion} style={{ width: '3rem' }} />
          <Column field="agent" header="Poradca" body={r => boldNameFormatter(formatAgent(r.agent))} />
          <Column field="dept" header="Dlh" sortable body={r => `${formatBigDecimalIntoString(r.dept)} EUR`} />
          <Column
            field="remainingDept"
            header="Zostávajúci dlh"
            sortable
            body={r => `${formatBigDecimalIntoString(r.remainingDept)} EUR`}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default ReportTable;
